import { useCallback, useState } from 'react'
import { useImagePlanning } from './useCopywriting'
import type { 
  Asset, 
  Note, 
  SearchIntent, 
  SearchIntentResult, 
  AssetIndexSummary,
  CandidateAsset,
  PlanningStage,
  StageLog
} from '@/types'
import type { ImagePlan } from '@/types/pipeline'
import type { TemplateConfig } from '@/lib/imageUtils'
import { fetchWithAuth } from '@/lib/api'

export function usePipelinePlanner() {
  const { isPlanning: isPlanningLegacy, error: legacyError } = useImagePlanning()
  
  // 两阶段规划状态
  const [isPlanningLocal, setIsPlanningLocal] = useState(false)
  const [errorLocal, setErrorLocal] = useState<string | null>(null)
  const [stage, setStage] = useState<PlanningStage>('idle')
  const [stageLogs, setStageLogs] = useState<StageLog[]>([])
  const [searchIntents, setSearchIntents] = useState<SearchIntent[]>([])

  const addStageLog = useCallback((
    stage: PlanningStage, 
    message: string, 
    duration?: number,
    data?: any
  ) => {
    setStageLogs(prev => [...prev, {
      stage,
      message,
      duration,
      timestamp: Date.now(),
      data
    }])
  }, [])

  const getStageMessage = useCallback((stage: PlanningStage): string => {
    switch (stage) {
      case 'fetching_index':
        return '正在获取素材索引...'
      case 'generating_intent':
        return '正在分析笔记内容，生成搜索意图...'
      case 'searching_assets':
        return '正在检索相关素材...'
      case 'generating_plan':
        return '正在生成最终配图方案...'
      case 'completed':
        return '配图规划完成'
      case 'error':
        return '配图规划失败'
      default:
        return ''
    }
  }, [])

  /**
   * 两阶段配图规划（RAG 模式）
   */
  const planWithRAG = useCallback(
    async (
      note: Note,
      projectId: string,
      templateConfig: TemplateConfig,
      avoidDuplicates: boolean = false,
      imageCount?: number,
      customInstruction?: string
    ): Promise<ImagePlan> => {
      console.log(`[usePipelinePlanner] planWithRAG: noteId=${note.id}, avoidDuplicates=${avoidDuplicates}, imageCount=${imageCount}`);
      const startTime = Date.now()
      setIsPlanningLocal(true)
      setErrorLocal(null)
      setStageLogs([])
      setSearchIntents([])
      
      try {
        // ============================================================
        // 阶段 1: 获取素材索引
        // ============================================================
        setStage('fetching_index')
        const indexStart = Date.now()
        
        const indexResponse = await fetchWithAuth('/api/get-asset-index', {
          method: 'POST',
          body: JSON.stringify({ project_id: projectId }),
        })

        if (!indexResponse.ok) {
          throw new Error('获取素材索引失败')
        }

        const assetIndex: AssetIndexSummary = await indexResponse.json()
        const indexDuration = Date.now() - indexStart
        addStageLog('fetching_index', `获取素材索引成功（共 ${assetIndex.total_count} 张）`, indexDuration)

        // ============================================================
        // 阶段 2: 生成搜索意图（AI 阶段一）
        // ============================================================
        setStage('generating_intent')
        const intentStart = Date.now()

        const phase1Response = await fetchWithAuth('/api/plan-images-phase1', {
          method: 'POST',
          body: JSON.stringify({
            note_id: note.id,
            title: note.title,
            content: note.content,
            asset_index: assetIndex,
            template_config: templateConfig,
            image_count_min: imageCount || templateConfig.image_count_min,
            image_count_max: imageCount || templateConfig.image_count_max,
            custom_instruction: customInstruction,
          }),
        })

        if (!phase1Response.ok) {
          throw new Error('生成搜索意图失败')
        }

        const intentResult: SearchIntentResult = await phase1Response.json()
        setSearchIntents(intentResult.search_intents)
        const intentDuration = Date.now() - intentStart
        addStageLog(
          'generating_intent', 
          `生成 ${intentResult.search_intents.length} 个搜索意图`, 
          intentDuration,
          { intents: intentResult.search_intents }
        )

        // ============================================================
        // 阶段 3: 执行素材检索（并行调用）
        // ============================================================
        setStage('searching_assets')
        const searchStart = Date.now()

        const searchPromises = intentResult.search_intents.map(async (intent) => {
          const searchResponse = await fetchWithAuth('/api/search-assets', {
            method: 'POST',
            body: JSON.stringify({
              project_id: projectId,
              query: intent.search.query,
              folder_id: intent.search.folder_id,
              scene: intent.search.scene,
              mood: intent.search.mood,
              limit: intent.search.limit,
            }),
          })

          if (!searchResponse.ok) {
            console.warn(`搜索配图 ${intent.image_index} 的素材失败`)
            return { image_index: intent.image_index, assets: [] }
          }

          const searchResult = await searchResponse.json()
          return {
            image_index: intent.image_index,
            assets: searchResult.assets as CandidateAsset[]
          }
        })

        const searchResults = await Promise.all(searchPromises)
        
        // 转换为 Record 格式
        const candidateAssets: Record<number, CandidateAsset[]> = {}
        searchResults.forEach(result => {
          candidateAssets[result.image_index] = result.assets
        })

        const totalCandidates = Object.values(candidateAssets).reduce(
          (sum, assets) => sum + assets.length, 
          0
        )
        const searchDuration = Date.now() - searchStart
        addStageLog(
          'searching_assets', 
          `检索到 ${totalCandidates} 张候选素材`, 
          searchDuration
        )

        // ============================================================
        // 阶段 4: 生成最终配图方案（AI 阶段二）
        // ============================================================
        setStage('generating_plan')
        const planStart = Date.now()

        const phase2Response = await fetchWithAuth('/api/plan-images-phase2', {
          method: 'POST',
          body: JSON.stringify({
            note_id: note.id,
            title: note.title,
            content: note.content,
            search_intents: intentResult.search_intents,
            candidate_assets: candidateAssets,
            template_config: templateConfig,
            custom_instruction: customInstruction,
          }),
        })

        if (!phase2Response.ok) {
          throw new Error('生成配图方案失败')
        }

        const finalPlan: ImagePlan = await phase2Response.json()
        const planDuration = Date.now() - planStart
        addStageLog(
          'generating_plan', 
          `生成 ${finalPlan.images.length} 张配图方案`, 
          planDuration
        )

        // ============================================================
        // 完成
        // ============================================================
        setStage('completed')
        const totalDuration = Date.now() - startTime
        addStageLog('completed', `配图规划完成，总耗时 ${totalDuration}ms`, totalDuration)

        return finalPlan
      } catch (err) {
        setStage('error')
        const errorMessage = err instanceof Error ? err.message : '未知错误'
        setErrorLocal(errorMessage)
        addStageLog('error', errorMessage)
        throw err
      } finally {
        setIsPlanningLocal(false)
      }
    },
    [addStageLog]
  )

  const planSingle = useCallback(
    async (
      note: Note,
      _assets: Asset[],
      imageCount: number,
      templateConfig: TemplateConfig,
      avoidDuplicates: boolean = false,
      customInstruction?: string
    ): Promise<ImagePlan> => {
      // 统一切换到 RAG 模式
      console.log(`[usePipelinePlanner] 使用 RAG 模式进行规划`)
      return planWithRAG(note, note.project_id, templateConfig, avoidDuplicates, imageCount, customInstruction)
    },
    [planWithRAG]
  )

  const planBatch = useCallback(
    async (
      notes: Array<{ note_id: string; title: string; content: string }>,
      assets: Asset[],
      imageCount: number,
      templateConfig: TemplateConfig,
      avoidDuplicates: boolean = false,
      customInstruction?: string
    ): Promise<ImagePlan[]> => {
      console.log(`[usePipelinePlanner] planBatch (RAG): avoidDuplicates=${avoidDuplicates}`);
      
      setIsPlanningLocal(true)
      setErrorLocal(null)
      
      try {
        // 并行执行每篇笔记的 RAG 规划
        const promises = notes.map(noteInfo => {
          const dummyNote: Note = {
            id: noteInfo.note_id,
            title: noteInfo.title,
            content: noteInfo.content,
            project_id: (notes as any).project_id || (assets[0]?.project_id),
            status: 'planning',
            images: [],
            hashtags: [],
            style: '',
            created_at: Date.now()
          }
          return planWithRAG(dummyNote, dummyNote.project_id, templateConfig, avoidDuplicates, imageCount, customInstruction)
        })
        
        const results = await Promise.all(promises)
        return results
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '批量规划失败'
        setErrorLocal(errorMessage)
        throw err
      } finally {
        setIsPlanningLocal(false)
      }
    },
    [planWithRAG]
  )

  return {
    planSingle,
    planBatch,
    planWithRAG,
    isPlanning: isPlanningLocal || isPlanningLegacy,
    plannerError: errorLocal || legacyError,
    // 两阶段状态
    stage,
    stageMessage: getStageMessage(stage),
    stageLogs,
    searchIntents,
  }
}
